import localforage from "localforage";

export type AppTab = {
	title: string;
	fullPath: string;
	props?: Record<string, unknown>;
};

// Strips legacy persisted keys (older versions stored a serialized vnode under `component`)
const sanitizeTab = (tab: AppTab | null): AppTab | null =>
	tab == null ? null : { title: tab.title, fullPath: tab.fullPath, props: tab.props };

const previousTab = shallowRef<AppTab | null>(null);
// @ts-ignore stom gezanik
const activeTab: ShallowRef<AppTab> = useStorageAsync<AppTab | null>(
	"pulsefood-active-tab",
	null as AppTab | null,
	localforage,
	{
		shallow: true,
		serializer: {
			read: (value) => sanitizeTab(JSON.parse(value)),
			write: (value) => JSON.stringify(value),
		},
	},
);

// @ts-ignore stom gezanik
const sessionTabs: ShallowRef<AppTab[]> = useStorageAsync<AppTab[]>("pulsefood-session-tabs", [], localforage, {
	shallow: true,
	serializer: {
		read: (value) => (JSON.parse(value) ?? []).map(sanitizeTab),
		write: (value) => JSON.stringify(value),
	},
});

// Which user the persisted tabs belong to. Logging in as someone else happens without a page reload, so
// the tabs above would otherwise carry over to the next user — including records they may not be allowed
// to see. Storing the owner lets us tell a user switch apart from a page refresh (where the tabs must be
// restored).
// @ts-ignore stom gezanik
const tabsOwnerId: ShallowRef<string | null> = useStorageAsync<string | null>(
	"pulsefood-tabs-owner",
	null,
	localforage,
);

import { Component, defineAsyncComponent, markRaw, readonly, ShallowRef } from "vue";
import { AsyncComponentLoader } from "@vue/runtime-core";
import router from "~/plugins/VueRouter.ts";
import { useUserStore } from "~stores/User.ts";
import { hideAllPoppers } from "floating-vue";

// Cached per glob loader so the wrapper identity is stable across re-renders;
// a fresh wrapper per render would remount every lazy tab on each App re-render.
const asyncComponentCache = new WeakMap<AsyncComponentLoader, Component>();

/**
 * Resolve a tab's component from the router at render time (the router-view
 * pattern). Tabs only persist serializable data, so HMR-updated component
 * definitions are always picked up on the next render.
 */
export function resolveTabComponent(tab: AppTab): Component | null {
	const comp = router.resolve(tab.fullPath).matched[0]?.components?.default;
	if (!comp) return null; // effectively unreachable: the catch-all route matches everything

	if (typeof comp === "function") {
		const loader = comp as AsyncComponentLoader;
		const cached = asyncComponentCache.get(loader);
		if (cached) return cached;

		const wrapped: Component = markRaw(defineAsyncComponent(loader));
		asyncComponentCache.set(loader, wrapped);
		return wrapped;
	}
	return comp as Component;
}

/**
 * Composable function to manage the application tabs
 * Tabs are added by vue-router navigation guards
 */
export const useAppTabs = () => {
	// const { push, currentRoute, back, previousUrl } = useRouter();
	const { activeUser } = storeToRefs(useUserStore());

	// Drop tabs that belong to a different user; keep them when the same user just refreshed the page.
	watch(
		() => activeUser.value?.id,
		(userId) => {
			if (userId == null) return;

			if (tabsOwnerId.value != null && tabsOwnerId.value !== userId) {
				sessionTabs.value = [];
				// @ts-ignore stom gezanik (activeTab is declared non-null, see its declaration above)
				activeTab.value = null;
				previousTab.value = null;
			}

			tabsOwnerId.value = userId;
		},
		{ immediate: true },
	);

	const addTab = (tab: AppTab) => {
		hideAllPoppers();

		previousTab.value = activeTab.value;
		activeTab.value = tab;

		if (activeUser.value?.settings?.tabDropdown == true) {
			sessionTabs.value = [tab, ...sessionTabs.value];
		} else {
			sessionTabs.value = [...sessionTabs.value, tab];
		}
	};

	/**
	 * @param navigate - Whether to automatically navigate to the previous tab after closing the current tab or to '/' if there are no tabs left
	 */
	const removeTab = (index: number, navigate: boolean = true) => {
		const closedActiveTab = activeTab.value.fullPath === sessionTabs.value[index].fullPath;
		sessionTabs.value = sessionTabs.value.toSpliced(index, 1);

		if (!closedActiveTab) {
			return;
		}

		if (previousTab.value && sessionTabs.value.some((tab) => tab.fullPath === previousTab.value.fullPath)) {
			activeTab.value = previousTab.value;
			previousTab.value = null;
			return;
		}

		if (sessionTabs.value.length === 0) {
			activeTab.value = null;
		} else {
			activeTab.value = sessionTabs.value[0];
		}

		previousTab.value = null;
	};

	/**
	 * Reorder tabs by moving the tab at `fromIndex` to `toIndex`. Reassigns a new array (rather than an
	 * in-place swap) so the shallowRef reacts and the new order is persisted.
	 */
	const moveTab = (fromIndex: number, toIndex: number) => {
		const tabs = sessionTabs.value;
		if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= tabs.length || toIndex >= tabs.length) {
			return;
		}

		const reordered = [...tabs];
		const [moved] = reordered.splice(fromIndex, 1);
		reordered.splice(toIndex, 0, moved);
		sessionTabs.value = reordered;
	};

	// DEPRECATED: if you use this, it will set the activeTab to the wrong title when you refresh
	const setCurrentTabTitle = (...args: string[]) => {
		const tab = activeTab.value;

		setTabTitle(tab?.fullPath, ...args);
	};

	const setTabTitle = (fullPath: string | null, ...args: string[]) => {
		if (!fullPath) {
			return;
		}

		const tab = sessionTabs.value.find((t) => t.fullPath === fullPath);
		if (!tab) {
			return;
		}
		tab.title = args.join(" ");
		sessionTabs.value = [...sessionTabs.value];
	};

	/**
	 * @param navigate - Whether to automatically navigate to the previous tab after closing the current tab or to '/' if there are no tabs left
	 */
	const closeCurrentTab = async (navigate: boolean = true) => {
		hideAllPoppers();

		const index = sessionTabs.value.findIndex((t) => t.fullPath === activeTab.value?.fullPath);
		if (index !== -1) {
			await removeTab(index, navigate);
		}
	};

	const activateTab = (tab: AppTab) => {
		hideAllPoppers();

		previousTab.value = activeTab.value;
		activeTab.value = sessionTabs.value.find((it) => it.fullPath === tab.fullPath);
	};

	const goToHome = () => {
		hideAllPoppers();

		activeTab.value = null;
	};

	const activatePreviousTab = () => {
		hideAllPoppers();

		const currentTab = activeTab.value;
		activeTab.value = previousTab.value;
		previousTab.value = currentTab;
	};

	return {
		sessionTabs,
		setCurrentTabTitle,
		setTabTitle,
		removeTab,
		moveTab,
		activeTab: readonly(activeTab),
		previousTab: readonly(previousTab),
		addTab,
		activateTab,
		activatePreviousTab,
		closeCurrentTab,
		goToHome,
	};
};
